// Deployment Config admin — view + edit the singleton row in
// deployment_config (isp identity, states of operation, contacts).
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

type DeployCfg = {
  id: number;
  isp_name: string;
  isp_code: string;
  states_of_operation: string[];
  contact_email: string | null;
  contact_phone: string | null;
  created_at?: string;
  updated_at?: string;
};

const ALL_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

export default function DeploymentConfigPage() {
  const qc = useQueryClient();
  const { data: cfg, isLoading } = useQuery<DeployCfg | null>({
    queryKey: ['deployment-config'],
    queryFn: () => api.get('/bead/config'),
  });

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [states, setStates] = useState<string[]>([]);
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [dirty, setDirty] = useState(false);
  const [initialized, setInit] = useState(false);

  useEffect(() => {
    if (cfg && !initialized) {
      setName(cfg.isp_name ?? '');
      setCode(cfg.isp_code ?? '');
      setStates(cfg.states_of_operation ?? []);
      setEmail(cfg.contact_email ?? '');
      setPhone(cfg.contact_phone ?? '');
      setInit(true);
    }
  }, [cfg, initialized]);

  const saveMut = useMutation({
    mutationFn: () => api.put('/bead/config', {
      isp_name: name,
      isp_code: code,
      states_of_operation: states,
      contact_email: email || null,
      contact_phone: phone || null,
    }),
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['deployment-config'] });
    },
  });

  if (isLoading) return <div className="card"><div className="card__body">Loading…</div></div>;
  if (!cfg) return <div className="card"><div className="card__body">No deployment_config row found.</div></div>;

  const toggleState = (s: string) => {
    setDirty(true);
    setStates((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s].sort());
  };

  const SECTION: React.CSSProperties = {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
    padding: '14px 18px', marginBottom: 16,
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <div className="page__header">
        <div>
          <h1 className="page__title">Deployment</h1>
          <div className="page__subtitle">
            ISP identity, operating states, and contact information for this Milestone Build instance.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {dirty && <span style={{ fontSize: 11, color: '#b45309' }}>● unsaved</span>}
          <button
            className="btn btn--primary"
            disabled={!dirty || saveMut.isPending}
            onClick={() => saveMut.mutate()}
            style={{ padding: '6px 16px' }}
          >
            {saveMut.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {saveMut.isError && (
        <div style={{ ...SECTION, background: '#fee2e2', borderColor: '#fecaca', color: '#991b1b' }}>
          Save failed: {(saveMut.error as any)?.message ?? 'unknown'}
        </div>
      )}

      <div style={SECTION}>
        <h2 style={{ margin: 0, marginBottom: 8, fontSize: 14, fontWeight: 700 }}>ISP identity</h2>
        <table style={{ width: '100%', fontSize: 13 }}>
          <tbody>
            <tr>
              <td style={{ padding: '6px 8px', color: '#64748b', width: 180 }}>ISP name</td>
              <td style={{ padding: '6px 8px' }}>
                <input value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }}
                  style={{ width: 360, padding: '4px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3 }} />
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                  Display name shown in headers, reports, and emails. (e.g. "Vistabeam")
                </div>
              </td>
            </tr>
            <tr>
              <td style={{ padding: '6px 8px', color: '#64748b' }}>ISP code (slug)</td>
              <td style={{ padding: '6px 8px' }}>
                <input value={code} onChange={(e) => { setCode(e.target.value); setDirty(true); }}
                  style={{ width: 200, padding: '4px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3, fontFamily: 'ui-monospace, monospace' }} />
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                  Lowercase, alphanumeric + hyphen/underscore. Used for tenancy isolation, file paths, doc IDs.
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={SECTION}>
        <h2 style={{ margin: 0, marginBottom: 6, fontSize: 14, fontWeight: 700 }}>States of operation</h2>
        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
          Restricts BEAD project queries, soil overlay coverage, and state filters in the UI to these states.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 4 }}>
          {ALL_STATES.map((s) => {
            const on = states.includes(s);
            return (
              <button key={s} onClick={() => toggleState(s)}
                style={{
                  padding: '6px 0', fontSize: 11, fontWeight: 600,
                  background: on ? '#1e3a8a' : '#f1f5f9',
                  color: on ? '#fff' : '#475569',
                  border: '1px solid ' + (on ? '#1e3a8a' : '#cbd5e1'),
                  borderRadius: 3, cursor: 'pointer',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                {s}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 8 }}>
          Selected: <strong>{states.length === 0 ? '(none)' : states.join(', ')}</strong>
        </div>
      </div>

      <div style={SECTION}>
        <h2 style={{ margin: 0, marginBottom: 8, fontSize: 14, fontWeight: 700 }}>Contact</h2>
        <table style={{ width: '100%', fontSize: 13 }}>
          <tbody>
            <tr>
              <td style={{ padding: '6px 8px', color: '#64748b', width: 180 }}>Contact email</td>
              <td style={{ padding: '6px 8px' }}>
                <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setDirty(true); }}
                  placeholder="(optional)"
                  style={{ width: 320, padding: '4px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3 }} />
              </td>
            </tr>
            <tr>
              <td style={{ padding: '6px 8px', color: '#64748b' }}>Contact phone</td>
              <td style={{ padding: '6px 8px' }}>
                <input type="tel" value={phone} onChange={(e) => { setPhone(e.target.value); setDirty(true); }}
                  placeholder="(optional)"
                  style={{ width: 200, padding: '4px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3 }} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'right' }}>
        Last saved: {cfg.updated_at ? new Date(cfg.updated_at).toLocaleString() : '—'} · row id {cfg.id}
      </div>
    </div>
  );
}
